// src/components/TimeEntryForm.jsx - Mit automatischer Produkt-Auswahl beim Auftrag-Wechsel
import React, { useState, useEffect } from "react";
import * as DateUtils from "../utils/dateUtils.js";
import { getEmployeeDefaultProduct } from "../services/azureProjectAssignmentService";
import { getContractBookingCount } from "../services/apiService.js";
import { getProjectPhases } from "../services/azureProjectPhasesService";
import { getHrSettings } from "../services/azureHrService";
import { deriveEntryFields } from "../utils/timeEntryFields.js";

const TimeEntryForm = ({
  selectedCells,
  contracts,
  activityTypes = [],
  products = [],
  onSubmit,
  onCancel,
  onDelete,
  loading,
  error,
  selectedProject = null,
  selectedEmployee = null,
  localProjects = [],
  editingEntry = null,
}) => {
  const isEditMode = !!editingEntry;
  const [formData, setFormData] = useState({
    contractUuid: "",
    activityTypeUuid: "",
    productUuid: "",
    description: "",
    startDate: "",
    endDate: "",
    localOnly: true,
    localProjectCode: "",
    localPhaseId: "",
    localProjectName: "",
  });

  const [defaultProduct, setDefaultProduct] = useState(null);
  const [autoProductSelected, setAutoProductSelected] = useState(false);
  const [contractBookings, setContractBookings] = useState({});
  const [currentPhase, setCurrentPhase] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState(false);
  const [hrSettings, setHrSettings] = useState(null);

  // HR-Settings laden, um bei Ganztag-Aktivitäten Wochenenden und Feiertage zu überspringen
  useEffect(() => {
    const year = String(new Date().getFullYear());
    getHrSettings(year)
      .then(setHrSettings)
      .catch(() => setHrSettings(null));
  }, []);

  // Im Edit-Modus: Formular aus dem bestehenden Eintrag vorbefuellen.
  // Die activityTypeUuid muss dem echten Typ entsprechen — wir matchen per typeName,
  // weil Azure-Eintraege nur den Namen speichern.
  useEffect(() => {
    if (!editingEntry) return;
    const fields = deriveEntryFields(editingEntry, activityTypes);
    setFormData((prev) => ({
      ...prev,
      ...fields,
      activityTypeUuid: fields.activityTypeUuid || prev.activityTypeUuid,
      startDate: editingEntry.date || "",
      endDate: editingEntry.date || "",
    }));
  }, [editingEntry, activityTypes]);

  // Berechne zusammenhängende Zeitblöcke
  // Im Edit-Modus eines stundenweisen Eintrags leiten wir den Block direkt
  // aus dem Eintrag ab (kein selectedCells vorhanden). Dauer fallback-weise
  // aus Start/End-Time berechnen, falls sie im Entry nicht persistiert ist.
  const durationFromTimes = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const diff = eh * 60 + em - (sh * 60 + sm);
    return diff > 0 ? diff / 60 : 0;
  };
  const timeBlocks =
    isEditMode && !editingEntry.isFullDay && editingEntry.startTime
      ? [
          {
            date: editingEntry.date,
            startTime: editingEntry.startTime,
            endTime: editingEntry.endTime,
            cells: [],
            duration:
              (Number(editingEntry.durationHours) || 0) +
              (Number(editingEntry.durationMinutes) || 0) / 60 ||
              durationFromTimes(editingEntry.startTime, editingEntry.endTime),
          },
        ]
      : DateUtils.getContiguousTimeBlocks(selectedCells);
  const totalHours = DateUtils.getTotalHoursFromBlocks(timeBlocks);

  // Prüfe ob Day Activity ausgewählt ist
  const isDayActivity = () => {
    const selectedActivityType = activityTypes.find(
      (at) => at.uuid === formData.activityTypeUuid
    );
    return selectedActivityType?.unit === "day";
  };

  // Automatische Produkt-Auswahl für Projektcontrolling
  useEffect(() => {
    const loadDefaultProduct = async () => {
      if (!selectedEmployee?.uuid || !selectedProject) {
        setDefaultProduct(null);
        setAutoProductSelected(false);
        return;
      }

      try {
        const productUuid = await getEmployeeDefaultProduct(
          selectedProject,
          selectedEmployee.uuid
        );

        if (productUuid) {
          setDefaultProduct(productUuid);
        } else {
          setDefaultProduct(null);
        }
      } catch (error) {
        setDefaultProduct(null);
      }
    };

    loadDefaultProduct();
  }, [selectedEmployee, selectedProject]);

  // Load phases for project and determine current phase.
  // Eine im Projekt-Dropdown explizit gewaehlte Phase hat Vorrang vor der
  // Datums-Schaetzung, sonst wuerde bei mehreren gleichzeitig aktiven Phasen
  // (z.B. "Ausschreibung" + "Vorprojekt") immer dieselbe Phase greifen,
  // egal was die Person tatsaechlich ausgewaehlt hat.
  useEffect(() => {
    const projectForPhases = selectedProject || formData.localProjectCode;

    const loadCurrentPhase = async () => {
      if (!projectForPhases) {
        setCurrentPhase(null);
        return;
      }

      setLoadingPhase(true);
      try {
        const phases = await getProjectPhases(projectForPhases);

        if (phases && phases.length > 0) {
          const chosenPhase = formData.localPhaseId
            ? phases.find((p) => p.id === formData.localPhaseId)
            : null;

          if (chosenPhase) {
            setCurrentPhase(chosenPhase);
            setLoadingPhase(false);
            return;
          }

          // Get today's date
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Find the phase that contains today's date
          const activePhase = phases.find(phase => {
            if (!phase.startDate || !phase.endDate) return false;
            const start = new Date(phase.startDate);
            const end = new Date(phase.endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
          });

          if (activePhase) {
            setCurrentPhase(activePhase);
          } else {
            // If no phase contains today, find the closest future phase
            const futurePhases = phases
              .filter(p => p.startDate && new Date(p.startDate) > today)
              .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

            if (futurePhases.length > 0) {
              setCurrentPhase(futurePhases[0]);
            } else {
              // Use the last phase if all are in the past
              const sortedPhases = phases
                .filter(p => p.endDate)
                .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
              setCurrentPhase(sortedPhases[0] || null);
            }
          }
        } else {
          setCurrentPhase(null);
        }
      } catch (error) {
        console.error("Error loading phases:", error);
        setCurrentPhase(null);
      }
      setLoadingPhase(false);
    };

    loadCurrentPhase();
  }, [selectedProject, formData.localProjectCode, formData.localPhaseId]);

  // Auto-select product from current phase for this employee (highest priority)
  useEffect(() => {
    if (!currentPhase || !selectedEmployee?.uuid || !isArbeitSelected()) {
      return;
    }

    const phaseProductUuid = currentPhase.personProducts?.[selectedEmployee.uuid];
    if (phaseProductUuid) {
      // Check if the product exists in our products list
      const product = products.find(p => p.uuid === phaseProductUuid);
      if (product && phaseProductUuid !== formData.productUuid) {
        setFormData((prev) => ({
          ...prev,
          productUuid: phaseProductUuid,
        }));
        setDefaultProduct(phaseProductUuid);
        setAutoProductSelected(true);
        console.log("✅ Produkt aus Phase automatisch ausgewählt:", product.productKey);
      }
    }
  }, [currentPhase, selectedEmployee, products, formData.activityTypeUuid]);

  // Automatische Produkt-Auswahl setzen wenn defaultProduct verfügbar
  useEffect(() => {
    if (defaultProduct && isArbeitSelected() && !autoProductSelected) {
      setFormData((prev) => ({
        ...prev,
        productUuid: defaultProduct,
      }));
      setAutoProductSelected(true);
    }
  }, [defaultProduct, formData.activityTypeUuid]);

  // NEUER USEEFFECT: Automatische Produkt-Auswahl beim Auftrag-Wechsel
  useEffect(() => {
    const loadProductForContract = async () => {
      if (
        !formData.contractUuid ||
        !selectedEmployee?.uuid ||
        !isArbeitSelected()
      ) {
        return;
      }

      // Finde das Projekt für den gewählten Auftrag
      const selectedContract = contracts.find(
        (c) => c.uuid === formData.contractUuid
      );
      const contractProject =
        selectedContract?.reference?.substring(0, 3) ||
        selectedContract?.projectReference;
      console.log("Hallo1:", contractProject);
      if (!contractProject) {
        return;
      }

      try {
        const productUuid = await getEmployeeDefaultProduct(
          contractProject,
          selectedEmployee.uuid
        );

        if (productUuid && productUuid !== formData.productUuid) {
          const product = products.find((prod) => prod.uuid === productUuid);
          if (product) {
            setFormData((prev) => ({
              ...prev,
              productUuid: productUuid,
            }));
            setAutoProductSelected(true);
            setDefaultProduct(productUuid);
          }
        }
      } catch (error) {
        console.error("❌ Fehler beim Laden des Produkts für Auftrag:", error);
      }
    };

    loadProductForContract();
  }, [
    formData.contractUuid,
    formData.activityTypeUuid,
    selectedEmployee,
    contracts,
    products,
  ]);

  // Automatische Vertrag-Auswahl basierend auf Projekt
  useEffect(() => {
    if (selectedProject && contracts.length > 0 && !formData.contractUuid) {
      const projectContract = contracts.find(
        (c) =>
          c.reference === selectedProject ||
          c.contract?.projectNumber === selectedProject
      );

      if (projectContract) {
        setFormData((prev) => ({
          ...prev,
          contractUuid: projectContract.uuid,
        }));
        console.log(
          "✅ Vertrag automatisch ausgewählt:",
          projectContract.title
        );
      }
    }
  }, [selectedProject, contracts, formData.contractUuid]);

  // Standard-Aktivitätstyp setzen
  useEffect(() => {
    if (activityTypes.length > 0 && !formData.activityTypeUuid) {
      const defaultActivityType = activityTypes.find(
        (at) => at.translations?.de?.description === "Arbeit"
      );

      if (defaultActivityType && !formData.activityTypeUuid) {
        setFormData((prev) => ({
          ...prev,
          activityTypeUuid: defaultActivityType.uuid,
        }));
      }
    }
  }, [activityTypes, formData.activityTypeUuid]);

  // Setze Start/End Date wenn Day Activity
  useEffect(() => {
    if (isDayActivity() && timeBlocks.length > 0 && !formData.startDate) {
      const dates = timeBlocks.map((block) => block.date).sort();
      setFormData((prev) => ({
        ...prev,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
      }));
    }
  }, [formData.activityTypeUuid]);

  // Standard-Produkt auswählen wenn "Arbeit" ausgewählt ist (Fallback)
  useEffect(() => {
    if (products.length > 0 && formData.activityTypeUuid && !defaultProduct) {
      const selectedActivityType = activityTypes.find(
        (at) => at.uuid === formData.activityTypeUuid
      );
      const isArbeitSelected =
        selectedActivityType?.translations?.de?.description === "Arbeit";

      if (isArbeitSelected && !formData.productUuid) {
        const fallbackProduct =
          products.find((p) => p.productKey === "A145") ||
          products.find((p) => p.productKey === "A150") ||
          products.find((p) => p.isService && !p.archivedAt) ||
          products[0];

        if (fallbackProduct) {
          setFormData((prev) => ({
            ...prev,
            productUuid: fallbackProduct.uuid,
          }));
        }
      }
    }
  }, [formData.activityTypeUuid, products, defaultProduct]);

  const loadContractBookingCount = async (contractUuid) => {
    if (contractBookings[contractUuid] !== undefined) return;

    const count = await getContractBookingCount(contractUuid);
    setContractBookings((prev) => ({ ...prev, [contractUuid]: count }));
  };

  const formatTimeBlocks = () => {
    if (isDayActivity()) {
      const dates = timeBlocks.map((block) => block.date).sort();
      if (dates.length === 1) {
        return new Date(dates[0]).toLocaleDateString("de-DE");
      }
      return `${new Date(dates[0]).toLocaleDateString("de-DE")} - ${new Date(
        dates[dates.length - 1]
      ).toLocaleDateString("de-DE")}`;
    }
    return DateUtils.formatTimeBlocks(timeBlocks);
  };

  const getBlocksInfo = () => {
    if (isDayActivity()) {
      const dates = timeBlocks.map((block) => block.date);
      const uniqueDates = [...new Set(dates)];
      return `${uniqueDates.length} Tag${uniqueDates.length > 1 ? "e" : ""}`;
    }

    const blockCount = timeBlocks.length;
    const dayCount = new Set(timeBlocks.map((block) => block.date)).size;

    let info = `${blockCount} Zeitblock${blockCount > 1 ? "e" : ""}`;
    if (dayCount > 1) {
      info += ` an ${dayCount} Tag${dayCount > 1 ? "en" : ""}`;
    }

    return info;
  };

  const getActivityTypeDisplayName = (activityType) => {
    const germanDescription = activityType.translations?.de?.description;
    if (germanDescription) {
      return `${germanDescription} (${activityType.activityNumber}) ${
        activityType.unit === "day" ? "[Ganztägig]" : ""
      }`;
    }
    return `Activity ${activityType.activityNumber} (${activityType.unit})`;
  };

  const getProductDisplayName = (product) => {
    const germanName = product.translations?.de?.productName;
    const germanDesc = product.translations?.de?.productDescription;

    if (germanName) {
      return `${product.productKey} - ${germanName}`;
    }
    return `${product.productKey} - ${
      germanDesc || product.productDescription || "Keine Beschreibung"
    }`;
  };

  const getContractDisplayName = (contract) => {
    if (contractBookings[contract.uuid] === undefined) {
      loadContractBookingCount(contract.uuid);
    }

    // Für TimeEntry: Reference + Title anzeigen (NICHT contractNumber)
    const fullReference = contract.reference || "";
    const contractTitle = contract.title || contract.displayTitle || "";

    // Falls selectedProject gesetzt ist, zeige es in eckigen Klammern
    const projectInfo = selectedProject ? `[${selectedProject}] ` : "";

    // Buchungsinfo hinzufügen (OHNE Warnung)
    const bookingCount = contractBookings[contract.uuid] || 0;
    const bookingInfo =
      bookingCount > 0
        ? ` (${bookingCount} Buchung${bookingCount > 1 ? "en" : ""})`
        : "";

    let displayName = "";

    // Priorität: 1. Reference + Title, 2. Nur Reference, 3. Nur Title
    if (fullReference && contractTitle) {
      displayName = `${projectInfo}${fullReference} - ${contractTitle}`;
    } else if (fullReference) {
      displayName = `${projectInfo}${fullReference}`;
    } else if (contractTitle) {
      displayName = `${projectInfo}${contractTitle}`;
    } else {
      // Fallback auf contractNumber nur wenn nichts anderes vorhanden
      const contractNumber =
        contract.number || contract.contractNoteNumber || "";
      displayName = `${projectInfo}${contractNumber || "Unbenannter Vertrag"}`;
    }

    return `${displayName}${bookingInfo}`;
  };

  const isArbeitSelected = () => {
    const selectedActivityType = activityTypes.find(
      (at) => at.uuid === formData.activityTypeUuid
    );
    return selectedActivityType?.translations?.de?.description === "Arbeit";
  };

  const isProductAutoSelected = () => {
    return autoProductSelected && formData.productUuid === defaultProduct;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isArbeitSelected() && !formData.localProjectCode) {
      alert("Bitte wählen Sie ein Projekt aus.");
      return;
    }
    if (!formData.activityTypeUuid) {
      alert("Bitte wählen Sie einen Aktivitätstyp aus.");
      return;
    }
    // productUuid is auto-selected for AbaNinja entries; not required for local entries
    if (isDayActivity() && (!formData.startDate || !formData.endDate)) {
      alert("Bitte wählen Sie Von- und Bis-Datum aus.");
      return;
    }


    // Für Day Activities: Erstelle einen Eintrag pro Arbeitstag im Zeitraum
    // (Wochenenden und Feiertage aus HR-Settings werden übersprungen)
    if (isDayActivity()) {
      const allDays = DateUtils.getDaysInRange(
        formData.startDate,
        formData.endDate
      );
      const feiertageSet = new Set(
        (hrSettings?.feiertage || []).map((f) =>
          typeof f === "string" ? f : f.date
        )
      );
      const betriebsferienSet = new Set(hrSettings?.betriebsferien || []);
      const workingDays = allDays.filter((dateStr) => {
        const dow = new Date(dateStr).getDay();
        if (dow === 0 || dow === 6) return false; // Wochenende
        if (feiertageSet.has(dateStr)) return false; // Feiertag
        if (betriebsferienSet.has(dateStr)) return false; // Betriebsferien (vorgearbeitet)
        return true;
      });

      if (workingDays.length === 0) {
        alert(
          "Im ausgewählten Zeitraum liegen keine Arbeitstage (nur Wochenenden/Feiertage)."
        );
        return;
      }

      const blocks = workingDays.map((date) => ({
        date,
        startTime: "00:00",
        endTime: "23:59",
        isFullDay: true,
      }));
      onSubmit({ ...formData, isFullDay: true, timeBlocks: blocks });
    } else {
      // Normale Zeitblock-Verarbeitung
      onSubmit({ ...formData, timeBlocks });
    }
  };

  return (
    <>
      <div className="overlay" onClick={onCancel}></div>
      <div className="entry-form">
        <h3>{isEditMode ? "Zeiteintrag bearbeiten" : "Zeiteintrag erstellen"}</h3>

        {error && <div className="alert error">{error}</div>}

        {/* Projekt-Info anzeigen */}
        {selectedProject && (
          <div className="project-info">
            <p>
              <strong>Projekt:</strong> {selectedProject}
            </p>
            {selectedEmployee && (
              <p>
                <strong>Mitarbeiter:</strong> {selectedEmployee.firstName}{" "}
                {selectedEmployee.lastName}
              </p>
            )}
            {loadingPhase ? (
              <p><em>Phase wird geladen...</em></p>
            ) : currentPhase ? (
              <p>
                <strong>Aktuelle Phase:</strong> {currentPhase.name}
                {selectedEmployee?.uuid && currentPhase.personProducts?.[selectedEmployee.uuid] && (() => {
                  const productUuid = currentPhase.personProducts[selectedEmployee.uuid];
                  const product = products.find(p => p.uuid === productUuid);
                  return product ? (
                    <span className="phase-rate-badge">
                      {" "}({product.productKey}: {product.cost} CHF/h)
                    </span>
                  ) : null;
                })()}
              </p>
            ) : null}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              {isDayActivity()
                ? "Ausgewählter Zeitraum:"
                : "Ausgewählte Zeitblöcke:"}
            </label>
            <textarea
              value={formatTimeBlocks()}
              disabled
              rows={isDayActivity() ? 1 : Math.max(3, timeBlocks.length)}
              style={{
                resize: "none",
                opacity: "1",
                color: "var(--text-color)",
              }}
            />
            <small
              style={{ color: "var(--text-bg-color)", fontStyle: "italic" }}
            >
              {getBlocksInfo()}{" "}
              {!isDayActivity() &&
                `• Gesamt: ${totalHours} Stunde${totalHours !== 1 ? "n" : ""}`}
            </small>
          </div>

          <div className="form-group">
            <label>Aktivitätstyp: *</label>
            <select
              value={formData.activityTypeUuid}
              onChange={(e) =>
                setFormData({ ...formData, activityTypeUuid: e.target.value })
              }
              required
              disabled={loading}
            >
              <option value="">Bitte wählen Sie einen Aktivitätstyp...</option>
              {activityTypes.map((activityType, idx) => (
                <option
                  key={`${activityType.uuid}-${activityType.activityNumber}-${idx}`}
                  value={activityType.uuid}
                >
                  {getActivityTypeDisplayName(activityType)}
                </option>
              ))}
            </select>
          </div>

          {isDayActivity() && (
            <>
              <div className="form-group">
                <label>Von Datum: *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Bis Datum: *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                  required
                  disabled={loading}
                  min={formData.startDate}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Projekt: {isArbeitSelected() && "*"}</label>
            <select
              value={
                formData.localProjectCode
                  ? `${formData.localProjectCode}::${formData.localPhaseId || ""}`
                  : ""
              }
              onChange={(e) => {
                const [projectCode, phaseId] = e.target.value.split("::");
                const localProject = localProjects.find(
                  (lp) => lp.code === projectCode && (lp.phaseId || "") === (phaseId || "")
                );
                setFormData({
                  ...formData,
                  contractUuid: "",
                  localOnly: true,
                  localProjectCode: projectCode,
                  localPhaseId: phaseId || "",
                  localProjectName: localProject?.title || projectCode,
                });
              }}
              required={isArbeitSelected()}
              disabled={loading || !isArbeitSelected()}
            >
              <option value="">
                {isArbeitSelected()
                  ? "Bitte wählen Sie ein Projekt..."
                  : "Nicht erforderlich für diesen Aktivitätstyp"}
              </option>
              {/* Fallback fuer Edit-Modus: urspruengliches Projekt anzeigen,
                  auch wenn es aktuell keine aktive Phase mehr hat. */}
              {formData.localProjectCode &&
                !localProjects.some(
                  (lp) =>
                    lp.code === formData.localProjectCode &&
                    (lp.phaseId || "") === (formData.localPhaseId || "")
                ) && (
                  <option
                    value={`${formData.localProjectCode}::${formData.localPhaseId || ""}`}
                  >
                    {formData.localProjectCode}
                    {formData.localProjectName
                      ? ` - ${formData.localProjectName}`
                      : ""}
                    {" (aktuell)"}
                  </option>
                )}
              {localProjects.map((lp) => (
                <option
                  key={`${lp.code}-${lp.phaseId || ""}`}
                  value={`${lp.code}::${lp.phaseId || ""}`}
                >
                  {lp.code} - {lp.title} {lp.phaseName ? `(${lp.phaseName})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Beschreibung:</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Beschreibung der durchgeführten Tätigkeiten..."
              disabled={loading}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Speichert..."
                : isEditMode
                ? "Änderungen speichern"
                : "1 Zeiteintrag speichern"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Abbrechen
            </button>
            {isEditMode && onDelete && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => onDelete(editingEntry)}
                disabled={loading}
                style={{ marginLeft: "auto" }}
              >
                Löschen
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default TimeEntryForm;
